import type { SupportedLocale } from '@/i18n/config';

export type DriveBreakGlassCopy = {
  breakGlassTitle: string;
  breakGlassDescription: string;
  breakGlassReason: string;
  breakGlassReasonPlaceholder: string;
  requestViewerBreakGlass: string;
  requestManagerBreakGlass: string;
  viewerBreakGlassGranted: string;
  managerBreakGlassGranted: string;
  breakGlassReauthRequired: string;
  breakGlassFailed: string;
};

const COPY: Record<SupportedLocale, DriveBreakGlassCopy> = {
  en: {
    breakGlassTitle: 'Emergency content access',
    breakGlassDescription:
      'Request audited, temporary access. Viewer access lasts one hour; Manager access is a separate escalation.',
    breakGlassReason: 'Reason for access',
    breakGlassReasonPlaceholder: 'Describe the incident or support need',
    requestViewerBreakGlass: 'Request 1-hour Viewer access',
    requestManagerBreakGlass: 'Escalate to Manager access',
    viewerBreakGlassGranted: 'Temporary Viewer access granted for one hour.',
    managerBreakGlassGranted: 'Temporary Manager access granted for one hour.',
    breakGlassReauthRequired:
      'Sign out and sign back in, then retry this request.',
    breakGlassFailed: 'Could not grant emergency access.',
  },
  'pt-BR': {
    breakGlassTitle: 'Acesso de conte\u00FAdo de emerg\u00EAncia',
    breakGlassDescription:
      'Solicita\u00E7\u00E3o auditada, acesso tempor\u00E1rio. O acesso ao espectador dura uma hora, o acesso ao gerente \u00E9 uma escalada separada.',
    breakGlassReason: 'Raz\u00E3o para o acesso',
    breakGlassReasonPlaceholder:
      'Descreva o incidente ou a necessidade de apoio.',
    requestViewerBreakGlass: 'Solicito acesso ao Visualizador em 1 hora.',
    requestManagerBreakGlass: 'Escalar para acesso do gerente.',
    viewerBreakGlassGranted:
      'Acesso tempor\u00E1rio ao Visualizador concedido por uma hora.',
    managerBreakGlassGranted:
      'Acesso tempor\u00E1rio ao gerente concedido por uma hora.',
    breakGlassReauthRequired: 'Assine de volta e tente novamente.',
    breakGlassFailed: 'N\u00E3o pude conceder acesso de emerg\u00EAncia.',
  },
  ar: {
    breakGlassTitle: 'وصول طارئ إلى المحتوى',
    breakGlassDescription:
      'اطلب وصولًا مؤقتًا ومدققًا. يستمر وصول المشاهد ساعة واحدة، ووصول المدير تصعيد منفصل.',
    breakGlassReason: 'سبب الوصول',
    breakGlassReasonPlaceholder: 'صِف الحادث أو حاجة الدعم',
    requestViewerBreakGlass: 'طلب وصول مشاهد لمدة ساعة',
    requestManagerBreakGlass: 'التصعيد إلى وصول المدير',
    viewerBreakGlassGranted: 'تم منح وصول مشاهد مؤقت لمدة ساعة.',
    managerBreakGlassGranted: 'تم منح وصول مدير مؤقت لمدة ساعة.',
    breakGlassReauthRequired: 'سجّل الخروج ثم الدخول وأعد المحاولة.',
    breakGlassFailed: 'تعذر منح الوصول الطارئ.',
  },
  de: {
    breakGlassTitle: 'Notfallzugriff auf Inhalte',
    breakGlassDescription:
      'Fordern Sie protokollierten, temporären Zugriff an. Betrachterzugriff gilt eine Stunde; Managerzugriff ist eine separate Eskalation.',
    breakGlassReason: 'Zugriffsgrund',
    breakGlassReasonPlaceholder: 'Vorfall oder Supportbedarf beschreiben',
    requestViewerBreakGlass: '1 Stunde Betrachterzugriff anfordern',
    requestManagerBreakGlass: 'Auf Managerzugriff eskalieren',
    viewerBreakGlassGranted:
      'Temporärer Betrachterzugriff für eine Stunde gewährt.',
    managerBreakGlassGranted:
      'Temporärer Managerzugriff für eine Stunde gewährt.',
    breakGlassReauthRequired:
      'Melden Sie sich ab und wieder an und versuchen Sie es erneut.',
    breakGlassFailed: 'Notfallzugriff konnte nicht gewährt werden.',
  },
  es: {
    breakGlassTitle: 'Acceso de emergencia al contenido',
    breakGlassDescription:
      'Solicita acceso temporal y auditado. El acceso de Lector dura una hora; el de Administrador es una escalación independiente.',
    breakGlassReason: 'Motivo del acceso',
    breakGlassReasonPlaceholder:
      'Describe el incidente o la necesidad de soporte',
    requestViewerBreakGlass: 'Solicitar acceso de Lector por 1 hora',
    requestManagerBreakGlass: 'Escalar a acceso de Administrador',
    viewerBreakGlassGranted:
      'Acceso temporal de Lector concedido por una hora.',
    managerBreakGlassGranted:
      'Acceso temporal de Administrador concedido por una hora.',
    breakGlassReauthRequired: 'Cierra sesión, vuelve a iniciarla y reintenta.',
    breakGlassFailed: 'No se pudo conceder el acceso de emergencia.',
  },
  fr: {
    breakGlassTitle: 'Accès d’urgence au contenu',
    breakGlassDescription:
      'Demandez un accès temporaire et audité. L’accès Lecteur dure une heure ; l’accès Gestionnaire est une escalade distincte.',
    breakGlassReason: 'Motif de l’accès',
    breakGlassReasonPlaceholder:
      'Décrivez l’incident ou le besoin d’assistance',
    requestViewerBreakGlass: 'Demander un accès Lecteur d’une heure',
    requestManagerBreakGlass: 'Passer à l’accès Gestionnaire',
    viewerBreakGlassGranted: 'Accès Lecteur temporaire accordé pour une heure.',
    managerBreakGlassGranted:
      'Accès Gestionnaire temporaire accordé pour une heure.',
    breakGlassReauthRequired:
      'Déconnectez-vous, reconnectez-vous, puis réessayez.',
    breakGlassFailed: 'Impossible d’accorder l’accès d’urgence.',
  },
  hi: {
    breakGlassTitle: 'आपातकालीन कंटेंट एक्सेस',
    breakGlassDescription:
      'ऑडिट किया गया अस्थायी एक्सेस माँगें। दर्शक एक्सेस एक घंटे का है; मैनेजर एक्सेस अलग एस्केलेशन है।',
    breakGlassReason: 'एक्सेस का कारण',
    breakGlassReasonPlaceholder: 'घटना या सहायता की आवश्यकता बताएँ',
    requestViewerBreakGlass: '1 घंटे का दर्शक एक्सेस माँगें',
    requestManagerBreakGlass: 'मैनेजर एक्सेस तक बढ़ाएँ',
    viewerBreakGlassGranted: 'एक घंटे के लिए अस्थायी दर्शक एक्सेस दिया गया।',
    managerBreakGlassGranted: 'एक घंटे के लिए अस्थायी मैनेजर एक्सेस दिया गया।',
    breakGlassReauthRequired:
      'साइन आउट करके फिर साइन इन करें और दोबारा प्रयास करें।',
    breakGlassFailed: 'आपातकालीन एक्सेस नहीं दिया जा सका।',
  },
  id: {
    breakGlassTitle: 'Akses konten darurat',
    breakGlassDescription:
      'Minta akses sementara yang diaudit. Akses Pelihat berlaku satu jam; akses Pengelola adalah eskalasi terpisah.',
    breakGlassReason: 'Alasan akses',
    breakGlassReasonPlaceholder: 'Jelaskan insiden atau kebutuhan dukungan',
    requestViewerBreakGlass: 'Minta akses Pelihat 1 jam',
    requestManagerBreakGlass: 'Eskalasi ke akses Pengelola',
    viewerBreakGlassGranted:
      'Akses Pelihat sementara diberikan selama satu jam.',
    managerBreakGlassGranted:
      'Akses Pengelola sementara diberikan selama satu jam.',
    breakGlassReauthRequired: 'Keluar lalu masuk kembali, kemudian coba lagi.',
    breakGlassFailed: 'Akses darurat tidak dapat diberikan.',
  },
  it: {
    breakGlassTitle: 'Accesso di emergenza ai contenuti',
    breakGlassDescription:
      'Richiedi un accesso temporaneo e verificato. L’accesso Visualizzatore dura un’ora; l’accesso Gestore è un’escalation separata.',
    breakGlassReason: 'Motivo dell’accesso',
    breakGlassReasonPlaceholder:
      'Descrivi l’incidente o la necessità di assistenza',
    requestViewerBreakGlass: 'Richiedi accesso Visualizzatore per 1 ora',
    requestManagerBreakGlass: 'Passa all’accesso Gestore',
    viewerBreakGlassGranted:
      'Accesso Visualizzatore temporaneo concesso per un’ora.',
    managerBreakGlassGranted: 'Accesso Gestore temporaneo concesso per un’ora.',
    breakGlassReauthRequired: 'Esci, accedi di nuovo e riprova.',
    breakGlassFailed: 'Impossibile concedere l’accesso di emergenza.',
  },
  ja: {
    breakGlassTitle: '緊急コンテンツアクセス',
    breakGlassDescription:
      '監査対象の一時アクセスを申請します。閲覧者アクセスは1時間、管理者アクセスは別のエスカレーションです。',
    breakGlassReason: 'アクセス理由',
    breakGlassReasonPlaceholder:
      'インシデントまたはサポートの必要性を説明してください',
    requestViewerBreakGlass: '1時間の閲覧者アクセスを申請',
    requestManagerBreakGlass: '管理者アクセスにエスカレーション',
    viewerBreakGlassGranted: '一時的な閲覧者アクセスが1時間付与されました。',
    managerBreakGlassGranted: '一時的な管理者アクセスが1時間付与されました。',
    breakGlassReauthRequired:
      'サインアウトして再度サインインし、もう一度お試しください。',
    breakGlassFailed: '緊急アクセスを付与できませんでした。',
  },
  ko: {
    breakGlassTitle: '긴급 콘텐츠 액세스',
    breakGlassDescription:
      '감사되는 임시 액세스를 요청합니다. 뷰어 액세스는 1시간이며 관리자 액세스는 별도 에스컬레이션입니다.',
    breakGlassReason: '액세스 사유',
    breakGlassReasonPlaceholder: '사고 또는 지원 필요 사항을 설명하세요',
    requestViewerBreakGlass: '1시간 뷰어 액세스 요청',
    requestManagerBreakGlass: '관리자 액세스로 에스컬레이션',
    viewerBreakGlassGranted: '임시 뷰어 액세스가 1시간 부여되었습니다.',
    managerBreakGlassGranted: '임시 관리자 액세스가 1시간 부여되었습니다.',
    breakGlassReauthRequired: '로그아웃한 후 다시 로그인하고 재시도하세요.',
    breakGlassFailed: '긴급 액세스를 부여할 수 없습니다.',
  },
  ru: {
    breakGlassTitle: 'Экстренный доступ к контенту',
    breakGlassDescription:
      'Запросите временный аудируемый доступ. Доступ Читателя действует час; доступ Менеджера — отдельное повышение.',
    breakGlassReason: 'Причина доступа',
    breakGlassReasonPlaceholder: 'Опишите инцидент или потребность в поддержке',
    requestViewerBreakGlass: 'Запросить доступ Читателя на 1 час',
    requestManagerBreakGlass: 'Повысить до доступа Менеджера',
    viewerBreakGlassGranted:
      'Временный доступ Читателя предоставлен на один час.',
    managerBreakGlassGranted:
      'Временный доступ Менеджера предоставлен на один час.',
    breakGlassReauthRequired: 'Выйдите, войдите снова и повторите запрос.',
    breakGlassFailed: 'Не удалось предоставить экстренный доступ.',
  },
  vi: {
    breakGlassTitle: 'Quyền truy cập nội dung khẩn cấp',
    breakGlassDescription:
      'Yêu cầu quyền truy cập tạm thời có kiểm toán. Quyền Người xem kéo dài một giờ; quyền Quản lý là bước nâng cấp riêng.',
    breakGlassReason: 'Lý do truy cập',
    breakGlassReasonPlaceholder: 'Mô tả sự cố hoặc nhu cầu hỗ trợ',
    requestViewerBreakGlass: 'Yêu cầu quyền Người xem trong 1 giờ',
    requestManagerBreakGlass: 'Nâng lên quyền Quản lý',
    viewerBreakGlassGranted: 'Đã cấp quyền Người xem tạm thời trong một giờ.',
    managerBreakGlassGranted: 'Đã cấp quyền Quản lý tạm thời trong một giờ.',
    breakGlassReauthRequired: 'Đăng xuất, đăng nhập lại rồi thử lại.',
    breakGlassFailed: 'Không thể cấp quyền truy cập khẩn cấp.',
  },
  zh: {
    breakGlassTitle: '紧急内容访问',
    breakGlassDescription:
      '申请经过审计的临时访问权限。查看者权限持续一小时；管理员权限需单独升级。',
    breakGlassReason: '访问原因',
    breakGlassReasonPlaceholder: '说明事件或支持需求',
    requestViewerBreakGlass: '申请 1 小时查看者权限',
    requestManagerBreakGlass: '升级为管理员权限',
    viewerBreakGlassGranted: '已授予一小时临时查看者权限。',
    managerBreakGlassGranted: '已授予一小时临时管理员权限。',
    breakGlassReauthRequired: '请退出并重新登录，然后重试。',
    breakGlassFailed: '无法授予紧急访问权限。',
  },
  'zh-TW': {
    breakGlassTitle: '緊急內容存取',
    breakGlassDescription:
      '申請經過稽核的暫時存取權。檢視者權限為一小時；管理員權限需另行提升。',
    breakGlassReason: '存取原因',
    breakGlassReasonPlaceholder: '說明事件或支援需求',
    requestViewerBreakGlass: '申請 1 小時檢視者權限',
    requestManagerBreakGlass: '提升為管理員權限',
    viewerBreakGlassGranted: '已授予一小時暫時檢視者權限。',
    managerBreakGlassGranted: '已授予一小時暫時管理員權限。',
    breakGlassReauthRequired: '請登出並重新登入，然後重試。',
    breakGlassFailed: '無法授予緊急存取權。',
  },
};

export function getDriveBreakGlassCopy(
  locale: SupportedLocale
): DriveBreakGlassCopy {
  return COPY[locale];
}
